// Last modified: 2026-08-22
import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, tap, finalize, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DomainLookupResponse, GoogleAuthError, GoogleNonceResponse, GoogleSignInGuidance, GoogleSignupData, isBlockedSignupEmailDomain, MicrosoftSignupData, OrgDomainMatch, SignupResult } from '../models/signup.model';
import { LanguageService } from './language.service';

// Define the structure of user credentials
export type LoginProvider = 'password' | 'microsoft' | 'google';

export interface UserCredentials {
  username: string;
  password: string;
}

export interface EmailVerificationPayload {
  token: string;
  username?: string;
  email?: string;
  password?: string;
}

// Define a more comprehensive user registration interface
export interface RegistrationData extends UserCredentials {
  email: string;
  full_name: string;
  text: string;
  company_name: string;
  language: string;
}

// Define the structure of the authentication response
export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  user: {
    id: string;
    // Backend sends `name`; `full_name` kept optional for legacy payloads.
    name?: string;
    full_name?: string;
    username: string;
    email: string;
    role?: string;
    is_active: boolean;
    email_verified?: boolean;
    email_verified_date?: string | null;
  };
}

// Define the structure for the current user
export interface User {
  id: string;
  username?: string;
  email: string;
  full_name: string;
  name?: string;
  role?: string;
  company_name?: string;
  org_id?: number;
  is_owner?:boolean;
  suser?:boolean;
  is_active:boolean;
  email_verified?: boolean;
  email_verified_date?: string | null;
  language?:string;
  department?:string;
  avatar?:string;
  author_credentials?: string;
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl: string = `${environment.apiBaseUrl}/auth`;
  private readonly TOKEN_KEY = 'authToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly USER_KEY = 'currentUser';
  private readonly LOGIN_PROVIDER_KEY = 'loginProvider';
  
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();
  
  private jwtHelper = new JwtHelperService();

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private injector: Injector
  ) {
    // Defer initializeAuth to avoid circular dependency:
    // AuthService → HttpClient → authInterceptor → AuthService
    Promise.resolve().then(() => this.initializeAuth());
  }

  // Initialize authentication state from local storage
  private initializeAuth(): void {
    const userData = localStorage.getItem(this.USER_KEY);
    if (userData) {
      try {
        const user = JSON.parse(userData);
        this.currentUserSubject.next(user);
        
        // Check if token is valid
        if (this.hasValidToken()) {
          this.isAuthenticatedSubject.next(true);
        } else {
          // Token expired, try to refresh or log out
          this.handleExpiredToken();
        }
      } catch (e) {
        console.error('Error parsing user data from localStorage', e);
        this.logout();
      }
    }
  }

  // Method to log in a user
  login(credentials: UserCredentials): Observable<AuthResponse> {
    this.isLoadingSubject.next(true);
    
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, credentials, this.httpOptions)
      .pipe(
        tap(response => {
          this.setLoginProvider('password');
          this.handleAuthSuccess(response);
        }),
        catchError(error => this.handleLoginError(error)),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  // Method to sign in with a Microsoft ID token
  microsoftSignIn(idToken: string): Observable<AuthResponse> {
    this.isLoadingSubject.next(true);

    return this.http.post<AuthResponse>(
      `${this.apiUrl}/microsoft-signin`,
      { id_token: idToken },
      this.httpOptions
    ).pipe(
      tap(response => {
        if ((response as any).success !== false) {
          this.setLoginProvider('microsoft');
          this.handleAuthSuccess(response);
        }
      }),
      catchError(error => this.handleError(error)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  createGoogleNonce(): Observable<GoogleNonceResponse> {
    return this.http.post<GoogleNonceResponse>(
      `${this.apiUrl}/google/nonce`,
      {},
      this.httpOptions
    ).pipe(
      catchError(error => this.handleGoogleAuthError(error))
    );
  }

  googleSignIn(idToken: string): Observable<AuthResponse | GoogleSignInGuidance> {
    this.isLoadingSubject.next(true);

    return this.http.post<AuthResponse | GoogleSignInGuidance>(
      `${this.apiUrl}/google-signin`,
      { id_token: idToken },
      this.httpOptions
    ).pipe(
      tap(response => {
        if (!this.isGoogleSignInGuidance(response)) {
          this.setLoginProvider('google');
          this.handleAuthSuccess(response);
        }
      }),
      catchError(error => this.handleGoogleAuthError(error)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  googleSignUp(data: GoogleSignupData): Observable<AuthResponse> {
    this.isLoadingSubject.next(true);

    return this.http.post<AuthResponse>(
      `${this.apiUrl}/google-signup`,
      data,
      this.httpOptions
    ).pipe(
      tap(response => {
        this.setLoginProvider('google');
        this.handleAuthSuccess(response);
      }),
      catchError(error => this.handleGoogleSignupError(error)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // Method to sign up a new user via Microsoft SSO
  microsoftSignUp(data: MicrosoftSignupData): Observable<AuthResponse> {
    this.isLoadingSubject.next(true);

    return this.http.post<AuthResponse>(
      `${this.apiUrl}/microsoft-signup`,
      data,
      this.httpOptions
    ).pipe(
      tap(response => {
        this.setLoginProvider('microsoft');
        this.handleAuthSuccess(response);
      }),
      catchError(error => this.handleMicrosoftSignupError(error)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // Method to sign up a new user
  signup(registrationData: RegistrationData): Observable<SignupResult> {
    this.isLoadingSubject.next(true);
    
    return this.http.post<SignupResult>(`${this.apiUrl}/signup`, registrationData, this.httpOptions)
      .pipe(
        tap(response => this.handleSignupSuccess(response)),
        catchError(error => this.handleSignupError(error)),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  verifyEmail(payload: EmailVerificationPayload): Observable<SignupResult> {
    this.isLoadingSubject.next(true);

    return this.http.post<SignupResult>(`${this.apiUrl}/verify-email`, payload, this.httpOptions)
      .pipe(
        catchError(error => this.handleVerificationError(error)),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  resendVerification(credentials?: Partial<UserCredentials>): Observable<SignupResult> {
    this.isLoadingSubject.next(true);

    return this.http.post<SignupResult>(`${this.apiUrl}/resend-verification`, credentials ?? {}, this.httpOptions)
      .pipe(
        catchError(error => this.handleVerificationError(error)),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  /**
   * Look up organisations that match a given email domain.
   *
   * Returns a DomainLookupResponse indicating whether the domain
   * is taken by an existing organisation. When `taken` is true,
   * the user should be blocked from proceeding with signup.
   *
   * Skips the call entirely for blocked signup email providers
   * (gmail.com, outlook.com, disposable domains, etc.) since they
   * will never match a corporate organisation.
   */
  lookupOrgByDomain(domain: string): Observable<DomainLookupResponse> {
    if (!domain || isBlockedSignupEmailDomain(domain)) {
      return of({ taken: false });
    }

    return this.http
      .get<DomainLookupResponse>(`${this.apiUrl}/org-lookup`, {
        ...this.httpOptions,
        params: { domain },
      })
      .pipe(
        catchError(() => {
          // Silently return not taken — org lookup is best-effort
          return of({ taken: false });
        })
      );
  }

  
  // Method to change password
  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    const payload = {
      username: this.getCurrentUser()?.username,
      old_password: currentPassword,
      new_password: newPassword
    };

    return this.http.post<any>(`${this.apiUrl}/reset`, payload)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }
  
  // Method to request password reset
  requestPasswordReset(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/request-password-reset`, { email }, this.httpOptions)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  // Method to refresh the token
  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    
    return this.http.post<AuthResponse>(`${this.apiUrl}/refresh`, { refresh_token: refreshToken }, this.httpOptions)
      .pipe(
        tap(response => this.handleAuthSuccess(response)),
        catchError(error => {
          console.error('Error refreshing token', error);
          this.logout();
          return throwError(() => error);
        })
      );
  }

  // Method to handle a successful signup response
  private handleSignupSuccess(response: SignupResult): void {
    // The backend returns { success: true, message: '...' }
    // No token is returned at signup — the user must log in afterwards.
    // We intentionally do NOT set isAuthenticated here.
  }

  private handleLoginError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'We are currently experiencing a high volume of traffic. Please try logging in again shortly.';
    const errorCode = error.error?.error;
    const detail = error.error?.detail || error.error?.message;

    if (error.error instanceof ErrorEvent) {
      errorMessage = 'Network error — please check your connection and try again.';
    } else if (error.status === 401) {
      errorMessage = 'Invalid username or password';
    } else if (error.status === 400 && errorCode === 'Email not verified') {
      errorMessage = 'Please verify your email before signing in.';
    } else if (error.status === 400) {
      errorMessage = detail || 'Invalid username or password';
    } else if (error.status >= 500) {
      errorMessage = 'We are currently experiencing a high volume of traffic. Please try logging in again shortly.';
    }

    console.error('Login error:', errorMessage, error);
    return throwError(() => ({ message: errorMessage, status: error.status, error: errorCode, detail }));
  }

  /**
   * Dedicated error handler for the signup flow.
   * Maps backend error responses to user-friendly messages.
   */
  private handleSignupError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred during registration. Please try again.';
    const errorCode = error.error?.error || error.error?.code;

    if (error.error instanceof ErrorEvent) {
      // Client-side / network error
      errorMessage = 'Network error — please check your connection and try again.';
    } else if (errorCode === 'blocked_email_domain') {
      errorMessage = 'This email domain is not eligible for signup. Please use your work email address.';
    } else if (error.status === 400) {
      // Backend returns 400 for "Username already registered"
      errorMessage = error.error?.detail || error.error?.message || 'This email is already registered. Please sign in instead.';
    } else if (error.status === 422) {
      errorMessage = 'Please check your input and try again.';
    } else if (error.status >= 500) {
      errorMessage = 'Our servers are temporarily unavailable. Please try again shortly.';
    }

    console.error('Signup error:', errorMessage, error);
    return throwError(() => ({ message: errorMessage, status: error.status, field: error.error?.field, error: errorCode }));
  }

  private isGoogleSignInGuidance(response: AuthResponse | GoogleSignInGuidance): response is GoogleSignInGuidance {
    return 'success' in response && response.success === false;
  }

  private handleGoogleAuthError(error: HttpErrorResponse): Observable<never> {
    const reason = error.error?.reason || error.error?.error || error.error?.code;
    const detail = error.error?.detail || error.error?.message;
    let message = detail || 'Google sign-in failed. Please try again.';

    if (error.error instanceof ErrorEvent || error.status === 0) {
      message = 'Network error — please check your connection and try again.';
    } else if (error.status === 401) {
      message = detail || 'Google sign-in expired or could not be verified. Please try again.';
    } else if (error.status === 429) {
      message = detail || 'Too many Google sign-in attempts. Please wait a moment and try again.';
    } else if (error.status === 503) {
      message = detail || 'Google sign-in is temporarily unavailable. Please try again shortly.';
    }

    const googleError: GoogleAuthError = {
      message,
      status: error.status,
      reason,
      detail
    };
    return throwError(() => googleError);
  }

  private handleGoogleSignupError(error: HttpErrorResponse): Observable<never> {
    const reason = error.error?.reason || error.error?.error || error.error?.code;
    const detail = error.error?.detail || error.error?.message;
    let message = detail || 'Google sign-up failed. Please try again.';

    if (error.error instanceof ErrorEvent || error.status === 0) {
      message = 'Network error — please check your connection and try again.';
    } else if (error.status === 401) {
      message = detail || 'Google sign-up expired or could not be verified. Please try again.';
    } else if (error.status === 409) {
      message = detail || 'This Google account is already registered. Please sign in instead.';
    } else if (error.status === 422) {
      message = detail || 'Please check your profile details and try again.';
    } else if (error.status === 429) {
      message = detail || 'Too many Google sign-up attempts. Please wait a moment and try again.';
    } else if (error.status === 503) {
      message = detail || 'Google sign-up is temporarily unavailable. Please try again shortly.';
    }

    return throwError(() => ({ message, status: error.status, reason, detail } as GoogleAuthError));
  }

  private handleMicrosoftSignupError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred during registration. Please try again.';
    const errorCode = error.error?.error || error.error?.code;

    if (error.error instanceof ErrorEvent) {
      errorMessage = 'Network error — please check your connection and try again.';
    } else if (errorCode === 'blocked_email_domain') {
      errorMessage = 'This email domain is not eligible for signup. Please use your work email address.';
    } else if (error.status === 409) {
      errorMessage = error.error?.detail || 'This Microsoft account is already registered. Please sign in instead.';
    } else if (error.status === 401) {
      errorMessage = 'Microsoft sign-up failed. Please try again.';
    } else if (error.status === 422) {
      errorMessage = 'Please check your input and try again.';
    } else if (error.status >= 500) {
      errorMessage = 'Our servers are temporarily unavailable. Please try again shortly.';
    }

    console.error('Microsoft signup error:', errorMessage, error);
    return throwError(() => ({ message: errorMessage, status: error.status, error: errorCode }));
  }

  private handleVerificationError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Email verification failed. Please try again.';

    if (error.error instanceof ErrorEvent) {
      errorMessage = 'Network error — please check your connection and try again.';
    } else if (error.status === 400) {
      errorMessage = error.error?.detail || error.error?.message || 'Verification link is invalid or expired.';
    } else if (error.status === 401) {
      errorMessage = error.error?.detail || 'Please confirm your email and password to verify this account.';
    } else if (error.status === 403) {
      errorMessage = error.error?.detail || 'This verification link does not belong to this account.';
    } else if (error.status >= 500) {
      errorMessage = 'Our servers are temporarily unavailable. Please try again shortly.';
    }

    console.error('Email verification error:', errorMessage, error);
    return throwError(() => ({ message: errorMessage, status: error.status }));
  }

  // Method to handle authentication success
  private handleAuthSuccess(response: AuthResponse): void {
    if(response.access_token){
      // Store tokens
      this.storeToken(response.access_token);
    }

    if (response.refresh_token) {
      this.storeRefreshToken(response.refresh_token);
    }
    
    // Store user data — backend sends `name`; normalize to `full_name`
    // so the rest of the app (which reads full_name everywhere) works.
    const user: User = response.user as unknown as User;
    user.full_name = user.full_name || user.name || '';
    this.storeUser(user);
    this.injector.get(LanguageService).useLanguage(user.language).subscribe({
      error: error => console.error('Failed to apply user language', error)
    });
    
    // Update subjects
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
  }

  // Method to handle expired tokens
  private handleExpiredToken(): void {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    // Don't redirect to /login if the user is already navigating to a public page
    const publicPaths = ['/signup', '/login', '/sign-in', '/verify', '/verify-email'];
    const currentPath = window.location.pathname;
    const isOnPublicPage = publicPaths.some(p => currentPath.startsWith(p));

    if (refreshToken) {
      // Attempt to refresh the token
      this.refreshToken().subscribe({
        next: () => { /* Token refreshed successfully */ },
        error: () => this.logout(!isOnPublicPage)
      });
    } else {
      this.logout(!isOnPublicPage);
    }
  }

  // Method to store the authentication token in local storage
  private storeToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }
  
  // Method to store the refresh token in local storage
  private storeRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }
  
  // Method to store user data in local storage
  private storeUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  // Method to record which provider was used for the current login session
  private setLoginProvider(provider: LoginProvider): void {
    localStorage.setItem(this.LOGIN_PROVIDER_KEY, provider);
  }

  getLoginProvider(): LoginProvider | null {
    const provider = localStorage.getItem(this.LOGIN_PROVIDER_KEY);
    return provider === 'password' || provider === 'microsoft' || provider === 'google'
      ? provider
      : null;
  }

  isFederatedLogin(): boolean {
    const provider = this.getLoginProvider();
    return provider === 'microsoft' || provider === 'google';
  }

  // Method to check whether the current session was authenticated via Microsoft SSO
  // (as opposed to a local username/password login). Microsoft-SSO users have no
  // local password to reset.
  isMicrosoftLogin(): boolean {
    return localStorage.getItem(this.LOGIN_PROVIDER_KEY) === 'microsoft';
  }

  // Method to retrieve the authentication token from local storage
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }
  
  // Method to get the refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }
  
  // Method to get the current user
  getCurrentUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  updateCurrentUserRole(role: string): User | null {
    const user = this.getCurrentUser();
    if (!user) {
      return null;
    }

    const normalizedRole = role.toLowerCase();
    const updatedUser: User = {
      ...user,
      role,
      is_owner: normalizedRole === 'owner'
    };

    this.storeUser(updatedUser);
    this.currentUserSubject.next(updatedUser);
    return updatedUser;
  }

  private getNormalizedRole(user: User | null): string {
    return (user?.role || '').toLowerCase();
  }

  setLanguage(language: string){
    if(!language) return;
    const user = this.getCurrentUser();
    if(user){
      user.language = language;
      this.storeUser(user);
    }
  }
  get isOwner(){
    const user = this.getCurrentUser();
    if(user && (user.is_owner || this.getNormalizedRole(user) == 'owner')){
      return true;
    }
    return false;
  }
  get isSuperuser(){
    const user = this.getCurrentUser();
    if(user && (user.suser || this.getNormalizedRole(user) == 'superuser')){
      return true;
    }
    return false;
  }

  get canManagePlatformSettings(){
    return this.isOwner;
  }

  // Method to check if the user is logged in
  isLoggedIn(): boolean {
    return this.hasValidToken();
  }
  
  // Method to check if the token is valid and not expired
  private hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      // Check if token is expired
      return !this.jwtHelper?.isTokenExpired(token);
    } catch (error) {
      console.error('Error checking token validity', error);
      return false;
    }
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;
    
    return this.jwtHelper.isTokenExpired(token);
  }

  isRefreshTokenExpired(): boolean {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return true;
    
    return this.jwtHelper.isTokenExpired(refreshToken);
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }
  
  // Method to log out the user
  logout(redirectToLogin: boolean = true): void {
    // Clear all auth-related data from local storage
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.LOGIN_PROVIDER_KEY);

    // Update subjects
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);

    // Navigate to login unless caller opts out (e.g. user is already on a public page)
    if (redirectToLogin) {
      this.router.navigate(['/login']);
    }
  }
  
  // Method to get the user's role
  getUserRole(): string | undefined {
    const user = this.getCurrentUser();
    return user?.role;
  }
  
  // Method to check if the user has a specific role
  hasRole(role: string): boolean {
    const userRole = this.getUserRole();
    return userRole === role;
  }
  
  // Method to update user profile
  updateProfile(userData: Partial<User>): Observable<User> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
    
    return this.http.put<User>(`${environment.apiBaseUrl}/users/profile`, userData, { headers })
      .pipe(
        tap(updatedUser => {
          // Update stored user data
          const currentUser = this.getCurrentUser();
          if (currentUser) {
            const mergedUser = { ...currentUser, ...updatedUser };
            this.storeUser(mergedUser);
            this.currentUserSubject.next(mergedUser);
          }
        }),
        catchError(error => this.handleError(error))
      );
  }
  
  
  // Method to reset password with token
  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reset-password`, { 
      token, 
      new_password: newPassword 
    }, this.httpOptions)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  // Error handling method
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.status === 401) {
        errorMessage = 'Unauthorized: Please check your credentials';
        
        // If token expired during a request, try to refresh
        if (error.error?.message?.includes('expired')) {
          this.handleExpiredToken();
        }
      } else if (error.status === 403) {
        errorMessage = 'Forbidden: You do not have permission for this action';
      } else if (error.status === 404) {
        errorMessage = 'Resource not found';
      } else if (error.status === 422) {
        errorMessage = 'Validation error: Please check your input';
      } else if (error.status >= 500) {
        errorMessage = 'Server error: Please try again later';
      }
      
      // Use server error message if available
      if (error.error?.message) {
        errorMessage = error.error.message;
      }
    }
    
    console.error('Auth service error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}